import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SelectMode } from '@app/shared/datatable-page';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { SelectionDescription } from '@app/shared/models/selection-description';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { UnsavedChangesService } from '@app/shared/services/unsaved-changes-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, Subscription } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'portal-bulk-action-toolbar',
  templateUrl: './bulk-action-toolbar.component.html',
  styles: [],
  standalone: false,
})
export class BulkActionToolbarComponent<T> implements OnChanges, OnDestroy, OnInit {
  @Input()
  enabled: boolean = false;

  @Input()
  entries: T[];

  @Input()
  mode: SelectMode = SelectMode.Default;

  @Input()
  selectionDescription: SelectionDescription;

  @Output()
  close = new EventEmitter<void>();

  @Output()
  toggleSelection = new EventEmitter<void>();

  showClose: boolean = false;
  showToolbar: boolean = false;

  private subscriptions: { ctrlA: Subscription | null; escape: Subscription | null } = {
    ctrlA: null,
    escape: null,
  };

  private _entries$ = new Subject<T[]>();

  get entries$() {
    return this._entries$.asObservable();
  }

  constructor(
    private hotkeyService: HotkeyService,
    private unsavedChangesService: UnsavedChangesService,
    private dialogService: DialogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('entries' in changes) {
      this._entries$.next(changes.entries.currentValue ?? []);
    }

    if ('enabled' in changes) {
      if (changes.enabled.currentValue === true) {
        this.addKeyHandlers();
      } else {
        if (this.subscriptions.escape != null) {
          this.subscriptions.escape.unsubscribe();
          this.subscriptions.escape = null;
        }
      }
    }

    if ('selectionDescription' in changes) {
      this.updateShowToolbar(changes.selectionDescription.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.ctrlA?.unsubscribe();
    this.subscriptions.escape?.unsubscribe();
  }

  ngOnInit(): void {
    this.showClose = this.mode === SelectMode.Default;

    this.addKeyHandlers();

    this._entries$.pipe(untilDestroyed(this)).subscribe((entries) => {
      if (entries.length > 0) {
        this.unsavedChangesService.askForConfirmation();
      } else {
        this.unsavedChangesService.reset();
      }

      this.updateShowToolbar();
    });
  }

  addKeyHandlers(): void {
    this.subscribeToCtrlA();

    if (this.enabled) {
      this.subscribeToEscape();
    }
  }

  removeKeyHandlers(): void {
    this.subscriptions.ctrlA?.unsubscribe();
    this.subscriptions.escape?.unsubscribe();

    this.subscriptions.ctrlA = null;
    this.subscriptions.escape = null;
  }

  private subscribeToCtrlA(): void {
    if (this.subscriptions.ctrlA == null) {
      this.subscriptions.ctrlA = this.hotkeyService
        .addShortcut({
          ignore: (e) => e.target instanceof HTMLInputElement,
          keys: 'control.a',
          prevent: true,
        })
        .subscribe(() => {
          this.subscribeToEscape();
          this.toggleSelection.emit();
        });
    }
  }

  private subscribeToEscape(): void {
    if (this.subscriptions.escape == null) {
      this.subscriptions.escape = this.hotkeyService
        .addShortcut({
          ignore: (e) => e.target instanceof HTMLInputElement,
          keys: 'escape',
        })
        .subscribe((event) => {
          if (this.dialogService.hasOpenModals() === false) {
            event.preventDefault();

            this.subscriptions.escape.unsubscribe();
            this.subscriptions.escape = null;

            this.close.emit();
          }
        });
    }
  }

  private updateShowToolbar(selectionDescription = this.selectionDescription): void {
    if (this.enabled) {
      if (this.mode === SelectMode.Default) {
        this.showToolbar = true;
      } else if (this.mode === SelectMode.ShowOnlyForSelection) {
        this.showToolbar = true;
        // this.showToolbar = selectionDescription !== 'none';
      }
    } else {
      this.showToolbar = false;
    }
  }
}
